import React, { useState, useEffect } from "react";
import axios from "axios";
import { Dialog } from "@mui/material";
import { CheckCircle, Clock, AlertCircle, Plus, X, UserPlus, ChevronDown, ChevronUp } from "lucide-react";
import styles from "../hrms.module.scss";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

// ── Auth header ───────────────────────────────────────────────────
const ax = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || localStorage.getItem("platform_token") || ""}` }
});

export default function OnboardingDashboard() {
  const { T } = useTheme();
  const STATUS_COLOR = { Completed:T.green, "In Progress":T.cyan, Pending:T.muted };
  const CAT_COLOR = { Documentation:T.cyan, "IT Setup":T.purple, Training:T.amber, HR:T.green, Access:T.red, General:T.muted };

  const [list,      setList]      = useState([]);
  const [tasks,     setTasks]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [detail,    setDetail]    = useState(null);
  const [detailOpen,setDetailOpen]= useState(false);
  const [taskOpen,  setTaskOpen]  = useState(false);
  const [newTask,   setNewTask]   = useState({ title:"", category:"Documentation", description:"", is_mandatory:true });
  const [assignId,  setAssignId]  = useState("");
  const [employees, setEmployees] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [oRes, tRes, eRes] = await Promise.all([
        axios.get(APIConstant.onboarding,              ax()),
        axios.get(`${APIConstant.onboarding}tasks`,    ax()),
        axios.get(APIConstant.employees,               ax()),
      ]);
      setList(oRes.data     || []);
      setTasks(tRes.data    || []);
      setEmployees(eRes.data|| []);
    } catch(e) { console.error("[Onboarding]", e.response?.status, e.response?.data); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openDetail = async (empId) => {
    try {
      const r = await axios.get(`${APIConstant.onboarding}employee/${empId}`, ax());
      setDetail(r.data);
      setDetailOpen(true);
    } catch(e) { console.error(e); }
  };

  const updateRecord = async (recordId, status) => {
    try {
      await axios.put(`${APIConstant.onboarding}records/${recordId}`, { status }, ax());
      const r = await axios.get(`${APIConstant.onboarding}employee/${detail.employee_id}`, ax());
      setDetail(r.data);
      fetchAll();
    } catch(e) { console.error(e); }
  };

  const handleAssign = async () => {
    if (!assignId) return;
    setAssigning(true);
    try {
      await axios.post(`${APIConstant.onboarding}assign/${assignId}`, {}, ax());
      fetchAll();
      setAssignId("");
    } catch(e) { alert(e.response?.data?.detail || "Error assigning onboarding"); }
    finally { setAssigning(false); }
  };

  const handleAddTask = async () => {
    if (!newTask.title) return;
    try {
      await axios.post(
        `${APIConstant.onboarding}tasks?title=${encodeURIComponent(newTask.title)}&category=${newTask.category}&description=${encodeURIComponent(newTask.description||"")}&is_mandatory=${newTask.is_mandatory}`,
        {}, ax()
      );
      setTaskOpen(false);
      setNewTask({ title:"", category:"Documentation", description:"", is_mandatory:true });
      fetchAll();
    } catch(e) { console.error(e); }
  };

  const stats = {
    total:      list.length,
    completed:  list.filter(e => e.status === "Completed").length,
    inProgress: list.filter(e => e.status === "In Progress").length,
    notStarted: list.filter(e => e.status === "Not Started").length,
  };

  // Only show employees not already in the onboarding list
  const assignedIds = new Set(list.map(e => e.employee_id));
  const unassigned  = employees.filter(e => !assignedIds.has(e.id));

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>EMPLOYEE MANAGEMENT</div>
          <h2>Onboarding</h2>
          <p>Track new employee onboarding progress and tasks</p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.btnSecondary} onClick={() => setTaskOpen(true)}><Plus size={14}/> Add Task</button>
        </div>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols4}`}>
        {[
          { label:"Total Onboarding", val:stats.total,      color:"cyan"  },
          { label:"Completed",        val:stats.completed,  color:"green" },
          { label:"In Progress",      val:stats.inProgress, color:"amber" },
          { label:"Not Started",      val:stats.notStarted, color:"red"   },
        ].map(({ label, val, color }) => (
          <div key={label} className={`${styles.statCard} ${styles[color]}`}>
            <div className={styles.statVal}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Assign onboarding */}
      <div className={styles.card} style={{ marginBottom:20 }}>
        <div className={styles.cardHeader}><h3>Assign Onboarding to Employee</h3></div>
        <div className={styles.cardBody}>
          <div style={{ display:"flex", gap:12, alignItems:"flex-end" }}>
            <div style={{ flex:1 }}>
              <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Select Employee</label>
              <select value={assignId} onChange={e => setAssignId(e.target.value)}
                style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:assignId?T.text:T.muted, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif" }}>
                <option value="" style={{ background:T.opt_bg }}>
                  {unassigned.length === 0
                    ? employees.length === 0 ? "Loading employees…" : "All employees assigned ✓"
                    : `Choose employee… (${unassigned.length} remaining)`}
                </option>
                {unassigned.map(e => (
                  <option key={e.id} value={e.id} style={{ background:T.opt_bg, color:T.text }}>
                    {e.first_name} {e.last_name}{e.employee_code ? ` (${e.employee_code})` : ""}
                    {e.department_name ? ` — ${e.department_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <button className={styles.btnPrimary} onClick={handleAssign}
              disabled={!assignId || assigning || tasks.length === 0}
              style={{ height:38, opacity:(!assignId||assigning)?0.6:1 }}>
              <UserPlus size={14}/> {assigning ? "Assigning…" : `Assign (${tasks.length} tasks)`}
            </button>
          </div>
          {tasks.length === 0 && (
            <p style={{ margin:"8px 0 0", fontSize:12.5, color:T.red }}>
              ⚠️ No task templates found. Add tasks using the button above, or restart the backend to trigger seed.
            </p>
          )}
        </div>
      </div>

      {/* Employee onboarding list */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Employee Onboarding Status</h3>
          <span className={styles.cardMeta}>{tasks.length} task templates · {list.length} employees</span>
        </div>
        {loading ? (
          <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign:"center", padding:"50px 20px", color:T.muted, fontSize:14 }}>
            No onboarding records yet. Select an employee above and click Assign.
          </div>
        ) : list.map(emp => (
          <div key={emp.employee_id} style={{ borderBottom:`1px solid ${T.cyan}0F` }}>
            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 22px", cursor:"pointer" }}
              onClick={() => setExpanded(expanded === emp.employee_id ? null : emp.employee_id)}>
              <div style={{ width:38, height:38, borderRadius:10, background:`linear-gradient(135deg,${T.cyan},${T.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#fff", flexShrink:0 }}>
                {(emp.employee_name||"").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:T.text }}>{emp.employee_name}</div>
                <div style={{ fontSize:11.5, color:T.muted }}>
                  {emp.employee_code}{emp.department ? ` · ${emp.department}` : ""}
                  {emp.date_of_joining ? ` · Joined ${emp.date_of_joining}` : ""}
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:200 }}>
                <div style={{ flex:1, height:5, background:T.toggle_bg, borderRadius:5, overflow:"hidden" }}>
                  <div style={{ width:`${emp.progress_pct}%`, height:"100%", background:`linear-gradient(90deg,${T.cyan},${T.purple})`, borderRadius:5, transition:"width 0.4s" }}/>
                </div>
                <span style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace", color:T.cyan, minWidth:30 }}>{emp.progress_pct}%</span>
              </div>
              <span style={{ fontSize:11, fontWeight:600, padding:"3px 9px", borderRadius:20, minWidth:80, textAlign:"center",
                background:emp.status==="Completed"?`${T.green}1F`:emp.status==="In Progress"?`${T.cyan}1F`:T.toggle_bg,
                color:emp.status==="Completed"?T.green:emp.status==="In Progress"?T.cyan:T.muted,
              }}>{emp.status}</span>
              <button className={styles.btnSm} onClick={e => { e.stopPropagation(); openDetail(emp.employee_id); }}>View</button>
              {expanded === emp.employee_id ? <ChevronUp size={14} style={{ color:T.muted }}/> : <ChevronDown size={14} style={{ color:T.muted }}/>}
            </div>

            {/* Expanded task list */}
            {expanded === emp.employee_id && (
              <div style={{ padding:"0 22px 16px", background:T.card2 }}>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {(emp.records || []).map(r => (
                    <div key={r.id} style={{
                      background: r.status==="Completed"?`${T.green}0F`:T.toggle_bg,
                      border:`1px solid ${r.status==="Completed"?`${T.green}26`:`${T.cyan}0F`}`,
                      borderRadius:8, padding:"8px 12px", fontSize:12.5,
                      display:"flex", alignItems:"center", gap:8, minWidth:200,
                    }}>
                      {r.status === "Completed"
                        ? <CheckCircle size={13} style={{ color:T.green, flexShrink:0 }}/>
                        : <Clock       size={13} style={{ color:T.muted,  flexShrink:0 }}/>}
                      <span style={{ color:r.status==="Completed"?T.green:T.text, flex:1 }}>{r.task_title}</span>
                      <span style={{ fontSize:10, padding:"2px 6px", borderRadius:10, background:`${CAT_COLOR[r.task_category]||T.muted}20`, color:CAT_COLOR[r.task_category]||T.muted }}>
                        {r.task_category}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Task template list */}
      <div className={styles.card} style={{ marginTop:20 }}>
        <div className={styles.cardHeader}>
          <h3>Onboarding Task Templates</h3>
          <span className={styles.cardMeta}>{tasks.length} tasks</span>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.darkTable}>
            <thead><tr><th>#</th><th>Task</th><th>Category</th><th>Mandatory</th></tr></thead>
            <tbody>
              {tasks.map((t, i) => (
                <tr key={t.id}>
                  <td style={{ color:T.dim, fontFamily:"JetBrains Mono,monospace", fontSize:12 }}>{i+1}</td>
                  <td>
                    <div style={{ fontWeight:500 }}>{t.title}</div>
                    {t.description && <div style={{ fontSize:11.5, color:T.muted, marginTop:2 }}>{t.description}</div>}
                  </td>
                  <td><span style={{ fontSize:11, padding:"3px 9px", borderRadius:20, background:`${CAT_COLOR[t.category]||T.muted}20`, color:CAT_COLOR[t.category]||T.muted }}>{t.category}</span></td>
                  <td><span className={`${styles.badge} ${t.is_mandatory ? styles.red : styles.grey}`}>{t.is_mandatory?"Mandatory":"Optional"}</span></td>
                </tr>
              ))}
              {tasks.length === 0 && <tr className={styles.emptyRow}><td colSpan={4}>No task templates. Restart backend or add tasks manually.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm">
        {detail && (
          <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:540, fontFamily:"Outfit,sans-serif", overflow:"hidden", maxHeight:"80vh", display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>{detail.employee_name}</h3>
                <div style={{ fontSize:12, color:T.muted, marginTop:3 }}>
                  {detail.progress_pct}% complete · {detail.completed}/{detail.total_tasks} tasks
                </div>
              </div>
              <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setDetailOpen(false)}><X size={16}/></button>
            </div>
            <div style={{ overflowY:"auto", padding:20, display:"flex", flexDirection:"column", gap:8 }}>
              {(detail.records || []).map(r => (
                <div key={r.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", background:T.toggle_bg, border:`1px solid ${r.status==="Completed"?`${T.green}1F`:`${T.cyan}0F`}`, borderRadius:9 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500, color:r.status==="Completed"?T.green:T.text }}>{r.task_title}</div>
                    <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{r.task_category}{r.is_mandatory?" · Required":""}</div>
                  </div>
                  <select value={r.status} onChange={e => updateRecord(r.id, e.target.value)}
                    style={{ background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:7, color:STATUS_COLOR[r.status]||T.muted, fontSize:12, padding:"5px 10px", outline:"none", fontFamily:"Outfit,sans-serif", cursor:"pointer" }}>
                    <option style={{background:T.opt_bg}}>Pending</option>
                    <option style={{background:T.opt_bg}}>In Progress</option>
                    <option style={{background:T.opt_bg}}>Completed</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>

      {/* Add task modal */}
      <Dialog open={taskOpen} onClose={() => setTaskOpen(false)}>
        <div style={{ background:T.card, border:`1px solid ${T.cyan}33`, borderRadius:14, width:440, fontFamily:"Outfit,sans-serif", overflow:"hidden" }}>
          <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, background:`${T.cyan}0A`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>Add Onboarding Task</h3>
            <button style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }} onClick={() => setTaskOpen(false)}><X size={16}/></button>
          </div>
          <div style={{ padding:24, display:"flex", flexDirection:"column", gap:14 }}>
            {[
              { label:"Task Title *",  key:"title",       placeholder:"e.g. Submit ID proof" },
              { label:"Description",   key:"description", placeholder:"Optional details"     },
            ].map(({ label, key, placeholder }) => (
              <div key={key}>
                <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</label>
                <input value={newTask[key]} onChange={e => setNewTask({ ...newTask, [key]:e.target.value })} placeholder={placeholder}
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box" }}/>
              </div>
            ))}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Category</label>
                <select value={newTask.category} onChange={e => setNewTask({ ...newTask, category:e.target.value })}
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif" }}>
                  {["Documentation","IT Setup","Training","HR","Access","General"].map(c => <option key={c} style={{background:T.opt_bg}}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:11.5, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Mandatory?</label>
                <select value={String(newTask.is_mandatory)} onChange={e => setNewTask({ ...newTask, is_mandatory:e.target.value==="true" })}
                  style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif" }}>
                  <option value="true" style={{background:T.opt_bg}}>Mandatory</option>
                  <option value="false" style={{background:T.opt_bg}}>Optional</option>
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
              <button onClick={() => setTaskOpen(false)} className={styles.btnSecondary}>Cancel</button>
              <button onClick={handleAddTask} className={styles.btnPrimary} disabled={!newTask.title}>Add Task</button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
